"use client";

import Image from "next/image";
import React, { useState } from "react";
import { motion } from "motion/react";
import { useLocale, useTranslations } from "next-intl";

// Composant Star réutilisable
const StarIcon = ({ className = "" }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="1em"
        height="1em"
        viewBox="0 0 24 24"
        className={className}
    >
        <path
            fill="currentColor"
            d="M9.153 5.408C10.42 3.136 11.053 2 12 2s1.58 1.136 2.847 3.408l.328.588c.36.646.54.969.82 1.182s.63.292 1.33.45l.636.144c2.46.557 3.689.835 3.982 1.776c.292.94-.546 1.921-2.223 3.882l-.434.507c-.476.557-.715.836-.822 1.18c-.107.345-.071.717.001 1.46l.066.677c.253 2.617.38 3.925-.386 4.506s-1.918.051-4.22-1.009l-.597-.274c-.654-.302-.981-.452-1.328-.452s-.674.15-1.328.452l-.596.274c-2.303 1.06-3.455 1.59-4.22 1.01c-.767-.582-.64-1.89-.387-4.507l.066-.676c.072-.744.108-1.116 0-1.46c-.106-.345-.345-.624-.821-1.18l-.434-.508c-1.677-1.96-2.515-2.941-2.223-3.882S3.58 8.328 6.04 7.772l.636-.144c.699-.158 1.048-.237 1.329-.45s.46-.536.82-1.182z"
        />
    </svg>
);

// Composant StarRating réutilisable
const StarRating = ({ rating = 5, className = "" }) => (
    <div className={`flex items-center gap-0.5 ${className}`}>
        {[...Array(5)].map((_, i) => (
            <StarIcon
                key={i}
                className={`transition-colors duration-200 ${
                    i < rating
                        ? "drop-shadow-sm"
                        : "text-gray-300 dark:text-grayDark-600"
                }`}
                style={{
                    color: i < rating ? "#00b67a" : undefined,
                }}
            />
        ))}
    </div>
);

export const TestimonialTooltip = ({
    small = false,
    medium = false,
    center = false,
    // LE NOMBRE EXACT QUAND L'APPELANT SAIT LE LIRE. Sans lui, on retombe sur
    // NUMBER_OF_STUDENTS, une valeur arrondie figée dans la configuration qui
    // vieillit en silence entre deux déploiements. La page d'accueil, elle,
    // interroge le back : elle passe le compte réel, et la phrase perd son
    // « plus de » puisqu'elle n'approxime plus rien.
    nombre = null,
}) => {
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const locale = useLocale();
    const t = useTranslations();

    // Système de tooltip simplifié - plus besoin de variables complexes

    const people = [
        {
            id: 1,
            name: "Jérôme DELAUNEY",
            designation: t("TestimonialTooltip_jeromeDelauney"),
            image: "/images/site/people/jeromedelauney.jpg",
        },
        {
            id: 2,
            name: "Shompoth KHAN",
            designation: t("TestimonialTooltip_shompothKhan"),
            image: "/images/site/people/shompoth.jpeg",
        },
        {
            id: 3,
            name: "Mansour MAHAMAT-SALLE",
            designation: t("TestimonialTooltip_mansourMahamat"),
            image: "/images/site/people/mansour.jpg",
        },
        {
            id: 4,
            name: "Manon ECHE",
            designation: t("TestimonialTooltip_manonEche"),
            image: "/images/site/people/manoneche.jpeg",
        },
        {
            id: 5,
            name: "Alliance TSHINDAYI",
            designation: t("TestimonialTooltip_allianceTshindayi"),
            image: "/images/site/people/alliance.jpeg",
        },
    ];

    if (small) {
        // people.splice(3, 2);
    }

    return (
        <div
            className={`flex flex-row items-center w-full ${
                center ? "justify-center" : ""
            }`}
        >
            {people.map((testimonial, idx) => (
                <div
                    className={`${
                        small || medium ? "-mr-1.5" : "-mr-4"
                    } relative group`}
                    key={testimonial.name}
                    onMouseEnter={
                        small ? null : () => setHoveredIndex(testimonial.id)
                    }
                    onMouseLeave={small ? null : () => setHoveredIndex(null)}
                >
                    {/* Tooltip simple et bien positionné */}
                    {hoveredIndex === testimonial.id && (
                        <div className="absolute -top-20 left-1/2 transform -translate-x-1/2 z-50">
                            <motion.div
                                initial={{ opacity: 0, y: 10, scale: 0.8 }}
                                animate={{
                                    opacity: 1,
                                    y: 0,
                                    scale: 1,
                                    transition: {
                                        type: "spring",
                                        stiffness: 300,
                                        damping: 25,
                                    },
                                }}
                                exit={{ opacity: 0, y: 10, scale: 0.8 }}
                                className="bg-grayDark-900 text-white px-4 py-3 rounded-xl shadow-2xl border border-grayDark-700/50 backdrop-blur-sm"
                                style={{ whiteSpace: "nowrap" }}
                            >
                                {/* Contenu du tooltip */}
                                <div className="text-center">
                                    <div className="font-semibold text-sm text-white mb-1">
                                        {testimonial.name}
                                    </div>
                                    <div className="text-xs text-slate-300 font-light">
                                        {testimonial.designation}
                                    </div>
                                </div>

                                {/* Flèche vers le bas */}
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2">
                                    <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-grayDark-900"></div>
                                </div>

                                {/* Gradients décoratifs */}
                                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent" />
                                <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
                            </motion.div>
                        </div>
                    )}
                    <div className="relative">
                        <Image
                            height={small ? 24 : medium ? 32 : 50}
                            width={small ? 25 : medium ? 32 : 50}
                            src={testimonial.image}
                            alt={`Avatar de ${testimonial.name}`}
                            className={`object-cover !m-0 !p-0 object-top rounded-full ${
                                small
                                    ? "h-6 w-6"
                                    : medium
                                    ? "h-8 w-8"
                                    : "lg:h-14 h-10 lg:w-14 w-10"
                            } border-3 group-hover:scale-110 group-hover:z-30 border-white dark:border-grayDark-600 relative transition-all duration-300 ease-out shadow-lg group-hover:shadow-xl group-hover:border-primary/50`}
                        />
                        {/* Ring d'animation au hover */}
                        <div
                            className={`absolute inset-0 rounded-full border-2 border-primary/0 group-hover:border-primary/60 transition-all duration-300 ${
                                small
                                    ? "h-6 w-6"
                                    : medium
                                    ? "h-8 w-8"
                                    : "lg:h-14 h-10 lg:w-14 w-10"
                            } group-hover:scale-125`}
                        ></div>
                    </div>
                </div>
            ))}

            {!small && !medium && (
                <div className="pl-6 lg:pl-8 font-sourceSerif text-sm italic font-light dark:text-white flex flex-col gap-2">
                    {/* Section étoiles améliorée */}
                    {/* La note et les étoiles suivent la même source : une
                        baisse de note ne doit pas laisser cinq étoiles pleines. */}
                    <div className="flex items-center gap-2">
                        <StarRating
                            rating={Math.round(
                                Number(process.env.TRUSTPILOT_NOTE) || 5,
                            )}
                            className="text-lg"
                        />
                        <span className="text-xs text-gray-500 dark:text-grayDark-400 font-sans not-italic">
                            {Number(process.env.TRUSTPILOT_NOTE).toLocaleString(
                                locale,
                            )}
                            /5
                        </span>
                    </div>

                    {/* Texte principal avec meilleure typographie */}
                    <div className="text-gray-700 dark:text-grayDark-200 leading-relaxed">
                        <span className="font-medium not-italic text-gray-900 dark:text-white">
                            {nombre
                                ? t("TestimonialTooltip_title_exact", {
                                      number: Number(nombre).toLocaleString(
                                          locale,
                                      ),
                                  })
                                : t("TestimonialTooltip_title", {
                                      number: Number(
                                          process.env.NUMBER_OF_STUDENTS,
                                      ).toLocaleString(locale),
                                  })}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};
